import * as React from 'react';
import { useCallback, forwardRef, useEffect, useState } from 'react';

/** Date Format Library. */
import dayjs from 'dayjs';

/** Material UI Packages. */
import { IconButton, Typography, Box, OutlinedInput, TextField, Tooltip } from '@mui/material';
import { LocalizationProvider, DatePicker, DateTimePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { usePickersContext } from '@mui/x-date-pickers/hooks';

/** Material UI Icons. */
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

/** Internal Components. */
import { OPERATION_TIMEZONE, operationNow } from 'shared/lib/dateFormatter';
import { OperationPickersDay } from 'shared/ui/OperationPickersDay';

/** Date format used for keyboard entry in DateField. */
const DATE_FIELD_FORMAT = 'MM-DD-YYYY';

/** Strips non-digits from typed input and re-inserts the MM-DD-YYYY dashes. */
function formatTypedDate(raw) {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean).join('-');
}

/** Combines multiple refs into one callback ref (the picker needs its own ref on the same DOM node we forward). */
function useCombinedRef(...refs) {
    return useCallback(
        (node) => {
            refs.forEach((ref) => {
                if (!ref) return;
                if (typeof ref === 'function') ref(node);
                else ref.current = node;
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        refs
    );
}

/** Calendar-icon-only field, used so DatePicker still opens its picker via mouse click. */
const CalendarButtonField = forwardRef(function CalendarButtonField(props, ref) {
    const { id, disabled, InputProps } = props;
    const { onOpen } = usePickersContext();
    const combinedRef = useCombinedRef(ref, InputProps?.ref);
    return (
        <IconButton
            ref={combinedRef}
            id={id}
            size="small"
            disabled={disabled}
            onClick={onOpen}
            aria-label="Open calendar"
            sx={{ p: '4px' }}
        >
            <CalendarTodayIcon sx={{ fontSize: 15 }} />
        </IconButton>
    );
});

export default function InputField({
    label,
    sx,
    value,
    onChange,
    onFocus,
    onBlur,
    name,
    icon: IconComponent,
    labelStyle,
    inputStyle,
    error,
    disabled,
    inputRef,
    tooltip,
    placeholder,
    maxLength,
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '25px',
                ...sx,
            }}
        >
            <Typography
                sx={{
                    display: 'inline',
                    color: '#003a52',
                    fontSize: '13px',
                    fontWeight: 500,
                    width: '100px',
                    ...labelStyle,
                }}
            >
                {label}
            </Typography>
            <Box sx={{ flex: 1 }}>
                <OutlinedInput
                    name={name}
                    size="small"
                    value={value}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    error={error}
                    disabled={disabled}
                    inputRef={inputRef}
                    placeholder={placeholder}
                    inputProps={maxLength ? { maxLength } : undefined}
                    sx={{
                        height: '27px',
                        '&.Mui-focused': {
                            backgroundColor: 'white',
                        },
                        '&.MuiInputBase-input': {
                            bgcolor: 'white',
                        },
                        bgcolor: 'white',
                        ...inputStyle,
                    }}
                />
                {IconComponent && (
                    <Tooltip title={tooltip}>
                        <IconButton
                            sx={{
                                color: 'white',
                                bgcolor: 'primary.dark',
                                borderRadius: '15px',
                                p: '3px',
                                ml: '5px',
                                ':hover': { bgcolor: 'rgb(0,100,126)', color: 'white' },
                            }}
                        >
                            <IconComponent sx={{ fontSize: '15px' }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
}

export function DateField({ label, name, value, onChange, sx, width, labelStyle, error, disabled }) {
    const parsedValue = value && dayjs(value).isValid() ? dayjs(value) : null;
    const [inputText, setInputText] = useState(parsedValue ? parsedValue.format(DATE_FIELD_FORMAT) : '');

    /** Keep the typed text in sync when the value changes from outside (e.g. picking from the calendar). */
    useEffect(() => {
        setInputText(parsedValue ? parsedValue.format(DATE_FIELD_FORMAT) : '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const emitChange = (newValue) => onChange({ target: { name, value: newValue } });

    const handleTextChange = (event) => {
        const formatted = formatTypedDate(event.target.value);
        setInputText(formatted);

        const digitCount = formatted.replace(/-/g, '').length;
        if (digitCount === 0) {
            emitChange(null);
        } else if (digitCount === 8) {
            const parsed = dayjs(formatted, DATE_FIELD_FORMAT, true);
            emitChange(parsed.isValid() ? parsed : null);
        }
    };

    /** If the user leaves the year blank (only MM-DD typed), fill in the current year automatically. */
    const handleBlur = () => {
        const digitCount = inputText.replace(/-/g, '').length;
        if (digitCount === 4) {
            const completed = `${inputText}-${operationNow().format('YYYY')}`;
            const parsed = dayjs(completed, DATE_FIELD_FORMAT, true);
            if (parsed.isValid()) {
                setInputText(completed);
                emitChange(parsed);
                return;
            }
        }
        if (digitCount > 0 && digitCount !== 8) {
            setInputText(parsedValue ? parsedValue.format(DATE_FIELD_FORMAT) : '');
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '25px',
                ...sx,
            }}
        >
            <Typography
                sx={{
                    display: 'inline',
                    color: '#003a52',
                    fontSize: '13px',
                    fontWeight: 500,
                    width: '100px',
                    ...labelStyle,
                }}
            >
                {label}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', width }}>
                    <TextField
                        size="small"
                        placeholder={DATE_FIELD_FORMAT}
                        value={inputText}
                        onChange={handleTextChange}
                        onBlur={handleBlur}
                        error={Boolean(error)}
                        disabled={disabled}
                        fullWidth
                        InputProps={{ sx: { pr: '4px' } }}
                        sx={{
                            '& .MuiInputBase-input': {
                                p: '7px 10px',
                                height: '13px',
                                fontSize: '13px',
                            },
                        }}
                    />
                    <DatePicker
                        value={parsedValue}
                        onChange={(newValue) => emitChange(newValue)}
                        disabled={disabled}
                        slots={{ field: CalendarButtonField, day: OperationPickersDay }}
                        sx={{ position: 'absolute', right: '2px', top: '50%', transform: 'translateY(-50%)' }}
                    />
                </Box>
            </LocalizationProvider>
        </Box>
    );
}

export function DateTimeField({ label, name, value, onChange, sx, width, labelStyle, error, additional }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '25px',
                ...sx,
            }}
        >
            <Typography
                sx={{
                    display: 'inline',
                    color: '#003a52',
                    fontSize: '13px',
                    fontWeight: 500,
                    width: '100px',
                    ...labelStyle,
                }}
            >
                {label}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                    value={value}
                    onChange={(newValue) => onChange({ target: { name, value: newValue } })}
                    inputFormat="MM/DD/YY hh:mm A"
                    ampm={false}
                    // 담당자가 어느 나라에서 시각을 고르든 근무지(LA) 시각으로 해석한다.
                    timezone={OPERATION_TIMEZONE}
                    sx={{
                        '& .MuiInputBase-input': {
                            p: '7px 10px',
                            height: '13px',
                            fontSize: '13px',
                        },
                        width: width,
                    }}
                    renderInput={(params) => <TextField {...params} size="small" error={Boolean(error)} />}
                    {...additional}
                />
            </LocalizationProvider>
        </Box>
    );
}
export function TimeField({ label, name, value, onChange, sx, width, labelStyle, disableTime, error }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '25px',
                ...sx,
            }}
        >
            <Typography
                sx={{
                    display: 'inline',
                    color: '#003a52',
                    fontSize: '13px',
                    fontWeight: 500,
                    width: '100px',
                    ...labelStyle,
                }}
            >
                {label}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                    value={value}
                    onChange={(newValue) => onChange({ target: { name, value: newValue } })}
                    inputFormat="MM/DD/YY hh:mm A"
                    ampm={false}
                    // 담당자가 어느 나라에서 시각을 고르든 근무지(LA) 시각으로 해석한다.
                    timezone={OPERATION_TIMEZONE}
                    sx={{
                        '& .MuiInputBase-input': {
                            p: '7px 10px',
                            height: '13px',
                            fontSize: '13px',
                        },
                        width: width,
                    }}
                    disabled={disableTime}
                    renderInput={(params) => <TextField {...params} size="small" error={error} />}
                />
            </LocalizationProvider>
        </Box>
    );
}
